import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { SetStateAction, useCallback, useEffect, useRef, useState } from 'react'

const ModalContext = React.createContext<{
    modal: any
    setModal: React.Dispatch<SetStateAction<any>>
    unSetModal: () => void
}>({
    modal: undefined,
    setModal: undefined,
    unSetModal: undefined,
})

const Modal = ({ modal, unSetModal }) => {
    const panel = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && unSetModal()
        document.addEventListener('keydown', onKeyDown)
        return () => document.removeEventListener('keydown', onKeyDown)
    }, [unSetModal])

    // Hold the page still behind the overlay. Restores whatever the document had rather than
    // assuming '' — a page that was already locked by something else must stay locked.
    useEffect(() => {
        const previous = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previous
        }
    }, [])

    // Only a press that both starts and ends on the backdrop closes it, so a text selection
    // dragged out of the panel does not dismiss the work in progress.
    const backdropPressed = useRef(false)

    return (
        // The generous top padding gives the panel headroom below the fixed menu bar so the close
        // button is not crowded against the top of the window. The overlay itself scrolls, so a
        // tall dialog still reaches its full height.
        <div
            className='fixed z-[9999] top-0 left-0 w-full h-full bg-black/80 backdrop-blur-sm pointer-events-auto flex flex-row items-start justify-center overflow-auto p-4 pt-12 sm:p-6 sm:pt-24'
            onPointerDown={(e) => (backdropPressed.current = e.target === e.currentTarget)}
            onPointerUp={(e) => {
                if (backdropPressed.current && e.target === e.currentTarget) unSetModal()
                backdropPressed.current = false
            }}
        >
            {/*
              Top-aligned on purpose. Vertical centring re-positions the panel every time its
              content height changes, so switching between deposit and withdraw made the whole
              dialog jump up and down under the cursor.
            */}
            <div ref={panel} className='relative w-full max-w-xl'>
                {modal}
                <button
                    aria-label='Close'
                    className='absolute -top-3 -right-3 z-10 rounded-full h-9 w-9 border border-white/40 bg-black hover:bg-white/10 hover:border-white flex items-center justify-center transition-colors'
                    onClick={() => unSetModal()}
                >
                    <FontAwesomeIcon icon={faClose} />
                </button>
            </div>
        </div>
    )
}

export const ModalProvider = (props) => {
    const [modal, setModal] = useState()
    const unSetModal = useCallback(() => {
        setModal(undefined)
    }, [setModal])

    return (
        <ModalContext.Provider value={{ unSetModal, setModal }} {...props}>
            {props.children}
            {modal && <Modal modal={modal} unSetModal={unSetModal} />}
        </ModalContext.Provider>
    )
}

export const useModal = () => {
    return React.useContext(ModalContext)
}
